<!--
  For a mapbox gl documentation see https://www.mapbox.com/maplibre-gl-js/api/

  TODO: Consider making it so zoom-in/out needs Ctrl by default, unless fullscreen mode is active
-->

<template>
  <div ref="mapElement" class="map">
    <div v-if="state.loaded && map">
      <slot :map="map" />
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  ScaleControl,
  type RequestTransformFunction,
  Map as MapLibreMap,
  type MapOptions
} from 'maplibre-gl'

type RecursiveStringArray = string | RecursiveStringArray[]

const i18n = useI18n()
const { getStyleUrl } = useMapStyles()

const props = withDefaults(
  defineProps<{
    options?: Partial<MapOptions>
    showScale?: boolean
    mapStyle?: string
  }>(),
  {
    options: () => ({}),
    showScale: true,
    mapStyle: undefined
  }
)

const effectiveMapStyle = computed(() => props.mapStyle ?? getStyleUrl('street'))

const mapElement = ref<HTMLElement | null>(null)

let map: MapLibreMap | null = null

const state = reactive<{
  loaded: boolean
  maximize: boolean
  bearing: number
  is3D: boolean
  show3dHint: boolean
}>({
  loaded: false,
  maximize: false,
  bearing: 0,
  is3D: false,
  show3dHint: true
})

const emit = defineEmits([
  'onLoad',
  'moveend',
  'drag',
  'zoom',
  'pan',
  'move',
  'rotate',
  'pitch',
  'dblclick',
  'contextmenu'
])

watch(effectiveMapStyle, (style) => {
  map?.setStyle(style)
})

const createMap = () => {
  if (!mapElement.value) return

  const transformRequest: RequestTransformFunction = (url, resourceType) => {
    if (!resourceType) {
      return { url }
    }

    // Do any other transforms you want
    return { url }
  }

  try {
    map = new MapLibreMap({
      container: mapElement.value,
      style: effectiveMapStyle.value,
      transformRequest,
      maxZoom: 19.5,
      maxPitch: 85,
      canvasContextAttributes: {
        preserveDrawingBuffer: true
      },
      ...props.options
    })
  } catch (err) {
    // WebGL/extension unavailable on this device; log but don't crash the host page
    console.warn('Failed to create map', err)
    return
  }

  map.on('load', () => {
    setSymbolLayerLanguage(i18n.locale.value.split('-')[0])
  })

  if (props.showScale) {
    map.addControl(new ScaleControl({}), 'bottom-right')
  } // TODO: Make sure this supports imperial units; add toggle based on user settings

  map.on(
    'load',
    function () {
      state.loaded = true
      emit('onLoad', map)
    }.bind(this)
  )

  map.on('moveend', () => {
    emit('moveend', map)
  })

  map.on('drag', (event) => {
    emit('drag', event)
  })

  map.on('zoom', (event) => {
    emit('zoom', event)
  })

  map.on('pan', (event) => {
    emit('pan', event)
  })

  map.on('move', (event) => {
    if (!map) return

    state.bearing = map.getBearing()

    emit('move', event)
  })

  map.on('rotate', (event) => {
    emit('rotate', event)
  })

  map.on('pitch', (event) => {
    emit('pitch', event)
  })

  map.on('dblclick', (event) => {
    emit('dblclick', event)
  })

  map.on('contextmenu', (event) => {
    emit('contextmenu', event)
  })
}

// Recursively rewrites every string token in a text-field expression, applying
// the language replacements (e.g. `name:en` -> `name:de`), while preserving the
// base structure of the text-field expression.
const convertExpression = (
  replacements: Record<string, string>,
  pattern: RegExp,
  expression: unknown
): unknown => {
  if (typeof expression === 'string') {
    return expression.replace(pattern, (match) => replacements[match] ?? match)
  }

  if (Array.isArray(expression)) {
    return expression.map((entry) => convertExpression(replacements, pattern, entry))
  }

  return expression
}

const setSymbolLayerLanguage = (localeCode: string) => {
  const langCode = localeCode || 'en'
  const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

  const replacements: Record<string, string> = {
    'name:en': `name:${langCode}`,
    name_en: `name_${langCode}`
  }

  const pattern = new RegExp(Object.keys(replacements).map(escapeRegExp).join('|'), 'g')

  map?.getStyle().layers.forEach((layer) => {
    if (layer.type !== 'symbol') return

    const textField = map?.getLayoutProperty(layer.id, 'text-field')

    if (textField == null) return

    map?.setLayoutProperty(
      layer.id,
      'text-field',
      convertExpression(replacements, pattern, textField)
    )
  })
}

onMounted(() => {
  createMap()
})
</script>

<style>
@import 'maplibre-gl/dist/maplibre-gl.css';

.maplibregl-ctrl-attrib {
  @apply !tw-mr-5 !tw-rounded-lg tw-text-xs;

  line-height: inherit !important;
}

.maplibregl-ctrl-attrib-button {
  @apply !tw-rounded-lg;
}

.maplibregl-ctrl-scale {
  @apply tw-pointer-events-none !tw-mr-5;
}
</style>
